import React, { useEffect, useRef, useState } from 'react';
import { viabilidadeAPI } from '@/lib/api';
import { Loader2, RefreshCw, AlertTriangle, MapPin, Cable, Tag, CheckCircle2 } from 'lucide-react';
import { ServicoChip, HpChip, vendavelNaCategoria, CATEGORIAS_SERVICO } from './servicos';
import { PlanosClaro } from './PlanosClaro';
import { ModoBusca, BuscaPorRua, BuscaPorHp } from './BuscaEndereco';

const soDigitos = (v) => (v || '').replace(/\D/g, '');
const norm = (s) => (s || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Z0-9]+/g, ' ').trim();

const Caixa = ({ children, tom = 'neutro', testid, modo, onModo }) => (
  <div className={`rounded-lg border p-3 text-xs space-y-2 ${tom === 'alerta' ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20' : tom === 'ok' ? 'border-purple-200 bg-purple-50/60 dark:border-purple-800 dark:bg-purple-900/10' : 'border-dashed border-gray-300 dark:border-gray-700'}`} data-testid={testid}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-semibold flex items-center gap-1.5 text-gray-700 dark:text-gray-200"><Cable className="w-3.5 h-3.5 text-red-600" /> Viabilidade Claro (COM CABO)</p>
      {onModo && <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">Buscar por <ModoBusca value={modo} onChange={onModo} testid={`${testid}-modo`} /></span>}
    </div>
    {children}
  </div>
);

// Planos da cidade sem HP (quando ainda não há viabilidade consultada)
const PlanosDaCidade = ({ uf, cidade, tipoCliente, credito, onEscolherPlano, testid }) => {
  const [aberto, setAberto] = useState(false);
  if (!uf || !cidade) return null;
  return (
    <details className="pt-1" onToggle={(e) => setAberto(e.currentTarget.open)}>
      <summary className="cursor-pointer font-semibold text-purple-700 dark:text-purple-300 inline-flex items-center gap-1" data-testid={`${testid}-planos-cidade-toggle`}><Tag className="w-3 h-3" /> Ver planos e preços da Claro em {cidade}/{uf} (sem HP)</summary>
      {aberto && <div className="mt-2"><PlanosClaro uf={uf} cidade={cidade} servicos={null} tipoCliente={tipoCliente} credito={credito} onEscolherPlano={onEscolherPlano} compacto testid={`${testid}-planos-cidade`} /></div>}
    </details>
  );
};

// Viabilidade dentro da Nova Venda / Detalhar: por CEP + número (automático), pelo nome da rua ou direto pelo HP
export const ViabilidadeInline = ({ cep, numero, cidade, uf, complemento, planos, tipoCliente = 'RESIDENCIAL', credito = null, onEscolherPlano = null, onResultado, onPreencherEndereco = null, testid = 'viab-inline' }) => {
  const cepD = soDigitos(cep);
  const numD = soDigitos(numero);
  const pronto = cepD.length === 8 && !!numD && !!cidade && (uf || '').length === 2;
  const assinatura = pronto ? `${cepD}|${numD}|${norm(cidade)}|${(uf || '').toUpperCase()}` : '';

  const [modo, setModo] = useState('cep');
  const [manual, setManual] = useState({ uf: '', cidade: '' });
  const [busca, setBusca] = useState({ assinatura: '', carregando: false, res: null });
  const [hpSel, setHpSel] = useState('');
  const [detalhe, setDetalhe] = useState({ hp: '', carregando: false, res: null });
  const [mostrarPlanos, setMostrarPlanos] = useState(false);
  const cache = useRef({});
  const porCep = modo === 'cep';

  const limpar = () => { setBusca({ assinatura: '', carregando: false, res: null }); setHpSel(''); setDetalhe({ hp: '', carregando: false, res: null }); onResultado?.(null); };
  const trocarModo = (m) => { setModo(m); setManual({ uf: (uf || '').toUpperCase(), cidade: (cidade || '').toUpperCase() }); limpar(); };

  const buscar = async () => {
    setBusca({ assinatura, carregando: true, res: null }); setHpSel(''); setDetalhe({ hp: '', carregando: false, res: null }); onResultado?.(null);
    try {
      const res = (await viabilidadeAPI.complementos({ cep: cepD, numero: numD, cidade, uf, origem: 'nova_venda' })).data;
      cache.current[assinatura] = res;
      setBusca({ assinatura, carregando: false, res });
    } catch {
      setBusca({ assinatura, carregando: false, res: { status: 'erro', mensagem: 'Falha ao consultar a viabilidade' } });
    }
  };

  useEffect(() => {
    if (!porCep) return undefined;
    if (!assinatura) { if (busca.res) limpar(); return undefined; }
    if (cache.current[assinatura]) { setBusca({ assinatura, carregando: false, res: cache.current[assinatura] }); return undefined; }
    const t = setTimeout(buscar, 1500);
    return () => clearTimeout(t);
  }, [assinatura, porCep]); // eslint-disable-line

  // Escolha automática: único HP ou complemento digitado casa com um da lista
  useEffect(() => {
    const r = busca.res;
    if (!r || r.status !== 'ok' || hpSel) return;
    if (r.total === 1) { setHpSel(r.hps[0].hp); return; }
    const alvo = norm(complemento);
    if (alvo) {
      const achado = r.hps.find((h) => norm(h.complementos) === alvo) || r.hps.find((h) => norm(h.complementos).includes(alvo));
      if (achado) setHpSel(achado.hp);
    }
  }, [busca.res, complemento, hpSel]);

  const aplicarDetalhe = (d) => {
    onResultado?.(d.status === 'ok' ? d : null);
    if (d.status === 'ok' && !porCep) onPreencherEndereco?.(d);
  };

  useEffect(() => {
    const r = busca.res;
    if (!hpSel || !r || r.status !== 'ok') return undefined;
    let ativo = true;
    setDetalhe({ hp: hpSel, carregando: true, res: null });
    (async () => {
      try {
        const d = (await viabilidadeAPI.detalhe({ hp: hpSel, cidade_codigo: r.cidade?.codigo, cidade: r.cidade?.nome, uf: r.cidade?.uf, origem: 'nova_venda', busca: porCep ? 'cep' : 'rua' })).data;
        if (!ativo) return;
        setDetalhe({ hp: hpSel, carregando: false, res: d });
        aplicarDetalhe(d);
      } catch {
        if (ativo) setDetalhe({ hp: hpSel, carregando: false, res: { status: 'erro', mensagem: 'Falha ao consultar os serviços do HP' } });
      }
    })();
    return () => { ativo = false; };
  }, [hpSel]); // eslint-disable-line

  const d = detalhe.res;
  const r = busca.res;
  const cidadePlanos = r?.cidade?.nome || d?.cidade || (porCep ? cidade : manual.cidade);
  const ufPlanos = r?.cidade?.uf || d?.uf || (porCep ? uf : manual.uf);
  const planosCidade = !d && !detalhe.carregando && <PlanosDaCidade uf={ufPlanos} cidade={cidadePlanos} tipoCliente={tipoCliente} credito={credito} onEscolherPlano={onEscolherPlano} testid={testid} />;

  const cabecalhoManual = !porCep && (
    modo === 'rua'
      ? <BuscaPorRua uf={manual.uf} cidade={manual.cidade} onUf={(v) => setManual({ uf: v, cidade: '' })} onCidade={(v) => setManual((m) => ({ ...m, cidade: v }))} origem="nova_venda"
          onResultado={(res) => { setHpSel(''); setDetalhe({ hp: '', carregando: false, res: null }); setBusca({ assinatura: 'rua', carregando: false, res }); }} testid={`${testid}-rua`} />
      : <BuscaPorHp uf={manual.uf} cidade={manual.cidade} onUf={(v) => setManual({ uf: v, cidade: '' })} onCidade={(v) => setManual((m) => ({ ...m, cidade: v }))} origem="nova_venda"
          onResultado={(det) => { setBusca({ assinatura: 'hp', carregando: false, res: null }); setHpSel(''); setDetalhe({ hp: det.hp, carregando: false, res: det }); aplicarDetalhe(det); }} testid={`${testid}-hp`} />
  );

  if (porCep && cepD.length !== 8) {
    return <Caixa testid={testid} modo={modo} onModo={trocarModo}><p className="text-gray-500">Digite o CEP e o número do endereço — ou busque pelo nome da rua / HP.</p>{planosCidade}</Caixa>;
  }
  if (porCep && !pronto) return <Caixa testid={testid} modo={modo} onModo={trocarModo}><p className="text-gray-500">Preencha número, cidade e UF para consultar a viabilidade do endereço.</p>{planosCidade}</Caixa>;
  if (porCep && (busca.carregando || busca.assinatura !== assinatura)) return <Caixa testid={testid} modo={modo} onModo={trocarModo}><p className="text-gray-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Buscando complementos na Claro…</p></Caixa>;

  if (!porCep && !r && !d) return <Caixa testid={testid} modo={modo} onModo={trocarModo}>{cabecalhoManual}{planosCidade}</Caixa>;

  if (r && r.status !== 'ok') {
    return (
      <Caixa tom="alerta" testid={testid} modo={modo} onModo={trocarModo}>
        {cabecalhoManual}
        <p className="text-amber-700 dark:text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {r?.mensagem || 'Viabilidade não consultada'}</p>
        {porCep && <button type="button" onClick={buscar} className="inline-flex items-center gap-1 font-semibold text-purple-600 hover:underline" data-testid={`${testid}-tentar`}><RefreshCw className="w-3 h-3" /> Tentar de novo</button>}
      </Caixa>
    );
  }
  if (r && r.total === 0) {
    return (
      <Caixa tom="alerta" testid={testid} modo={modo} onModo={trocarModo}>
        {cabecalhoManual}
        <p className="text-amber-700 dark:text-amber-300">Nenhum HP com cabo encontrado para este número ({r.logradouros?.[0]?.logradouro || 'logradouro'}, {r.numero}). Confira o número ou o CEP.</p>
      </Caixa>
    );
  }
  const avisos = planos ? Object.keys(CATEGORIAS_SERVICO).filter((k) => planos[k] && d?.status === 'ok' && vendavelNaCategoria(d.servicos, k) === false) : [];
  return (
    <Caixa tom="ok" testid={testid} modo={modo} onModo={trocarModo}>
      {cabecalhoManual}
      {r && (
        <>
          <p className="text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.logradouros?.[0]?.logradouro}, {r.numero} • {r.cidade?.nome}/{r.cidade?.uf} • <b>{r.total}</b> complemento(s)</p>
          <div className="flex flex-wrap items-center gap-2">
            <select value={hpSel} onChange={(e) => setHpSel(e.target.value)} className="h-8 min-w-[220px] max-w-full rounded-md border bg-white dark:bg-gray-900 px-2 text-xs" data-testid={`${testid}-select`}>
              <option value="">Escolha o complemento…</option>
              {r.hps.map((h) => <option key={h.hp} value={h.hp}>{h.complementos || 'sem complemento'} — HP {h.hp}</option>)}
            </select>
            {detalhe.carregando && <span className="text-gray-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> consultando serviços…</span>}
            {d?.status === 'ok' && <HpChip hp={d.hp} testid={`${testid}-hp`} />}
          </div>
        </>
      )}
      {!r && d?.status === 'ok' && (
        <p className="text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {d.logradouro}, {d.numero}{d.complementos ? ` — ${d.complementos}` : ''} • {d.bairro} • {d.cidade}/{d.uf} • CEP {d.cep} <HpChip hp={d.hp} testid={`${testid}-hp`} /></p>
      )}
      {d && d.status !== 'ok' && <p className="text-amber-700 dark:text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {d.mensagem || 'Não foi possível consultar os serviços'}</p>}
      {d?.status === 'ok' && (
        <div className="space-y-1.5" data-testid={`${testid}-servicos`}>
          <div className="flex flex-wrap gap-1">{d.servicos.map((s) => <ServicoChip key={s.tipo} s={s} testid={`${testid}-servico-${s.tipo}`} />)}</div>
          <p className="text-[11px] text-gray-500">{d.vendaveis.length ? `${d.vendaveis.length} produto(s) vendável(is)` : 'Nenhum produto vendável neste HP'}{d.codificacao ? ` • ${d.codificacao}` : ''} • {porCep ? 'o HP foi preenchido no endereço' : 'HP e endereço preenchidos na venda'}</p>
          {!porCep && onPreencherEndereco && <p className="text-[11px] text-emerald-700 dark:text-emerald-300 font-semibold inline-flex items-center gap-1" data-testid={`${testid}-endereco-preenchido`}><CheckCircle2 className="w-3 h-3" /> Endereço da venda preenchido com os dados da Claro (CEP, rua, número, bairro, complemento e HP).</p>}
          {avisos.map((k) => (
            <p key={k} className="text-amber-700 dark:text-amber-300 font-semibold inline-flex items-center gap-1" data-testid={`${testid}-aviso-${k}`}><AlertTriangle className="w-3 h-3" /> {CATEGORIAS_SERVICO[k].label} escolhido na venda, mas a Claro marcou como NÃO vendável neste HP.</p>
          ))}
          <details className="pt-1" onToggle={(e) => setMostrarPlanos(e.currentTarget.open)}>
            <summary className="cursor-pointer font-semibold text-purple-700 dark:text-purple-300 inline-flex items-center gap-1" data-testid={`${testid}-planos-toggle`}><Tag className="w-3 h-3" /> Ver planos e preços da Claro nesta cidade</summary>
            {mostrarPlanos && <div className="mt-2"><PlanosClaro uf={ufPlanos} cidade={cidadePlanos} servicos={d.servicos} tipoCliente={tipoCliente} credito={credito} onEscolherPlano={onEscolherPlano} compacto testid={`${testid}-planos`} /></div>}
          </details>
        </div>
      )}
      {planosCidade}
    </Caixa>
  );
};
